/**
 * S10 — V2 schema helpers for the parent dashboard.
 *
 * Wires the parent dashboard to the (still-in-progress) Curriculum V2 schema
 * so we can show 108 課綱 codes + descriptions + 能力指標達成度 + 最近一週學了
 * 哪些單元.
 *
 * ────────────────────────────────────────────────────────────────────────
 * Source-of-truth dance (S1 → S10 transition window)
 * ────────────────────────────────────────────────────────────────────────
 *
 * S1 only shipped V2 *types* (src/learn/curriculum/v2/types.ts). The actual
 * runtime V2 data (CURRICULUM_V2: CurriculumV2) is S2; the moeMetadata.json
 * 50-200 字 description backfill is S3. Neither has merged when this module
 * was authored. Per the S10 task spec ("若 S3 metadata 尚未 ship → 用 V1 既
 * 有 moeCode 推估 + 標 pending"), we degrade gracefully:
 *
 *   1. **Primary** — if `src/learn/curriculum/v2/moe-metadata.json` exists
 *      (S3 ships it), we load it via a dynamic import that's resolved at
 *      module init. The lookup returns the canonical V2 fields.
 *   2. **Fallback** — walk V1 `CURRICULUM` to derive `moeContentCode` from
 *      `kp.moeCode`. `moePerformanceCode` is *estimated* from the moeCode
 *      prefix (e.g. `N-3-7` → `n-II-7` by lower-casing strand + swapping
 *      grade-digit for the roman-numeral 學習階段). Description is left
 *      empty and the row is flagged `pending: true`.
 *
 * Honesty principle (per project memory "晚說不如早說"): pending rows are
 * surfaced in the UI with a "(待 S3 補完)" marker, never silently faked.
 *
 * ────────────────────────────────────────────────────────────────────────
 * What this module exports
 * ────────────────────────────────────────────────────────────────────────
 *
 *   • lookupMoeMetadata(kpId)            → 教育部代碼 + description + source
 *   • buildAbilityIndicatorAchievement   → 「學生能 ___」達成率列表
 *   • buildRecentWeekUnits               → 「最近一週學了哪些單元」清單
 *
 * Pure data — no DOM. UI rendering lives in parent-dashboard-ui.ts.
 *
 * "學習與遊戲完全分離" — this module is in src/learn/ only, never imported
 * by src/game/.
 */

import type { PlayerProgress, Subject } from '../../shared/types';
import { CURRICULUM, type KPEntry, type ChapterEntry, type SubjectCurriculum } from '../curriculum';
import { masteryPercent } from '../../shared/student-kp';

// ────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────

/**
 * Result of {@link lookupMoeMetadata}. Fields mirror the V2 `KPV2` shape
 * but with explicit pending markers so the UI can degrade honestly while
 * S3 backfill is in flight.
 */
export interface MoeMetadata {
  /** Echo of the requested kpId. */
  kpId: string;
  /** 學習內容 code, e.g. `'N-3-9'`. `'pending'` when truly unknown. */
  moeContentCode: string;
  /**
   * 學習表現 code (動詞層), e.g. `'n-II-9'`. Undefined when neither the
   * V2 metadata nor the V1 estimator could derive one (rare — only for
   * KPs whose V1 moeCode is itself `'pending'` or missing).
   */
  moePerformanceCode?: string;
  /**
   * 50-200 字 NAER explanation. Empty string when S3 metadata has not
   * been backfilled for this kpId yet.
   */
  description: string;
  /** Display label (KP 標籤), used by tooltip header. */
  label: string;
  /** Which curriculum source produced this row. */
  source: 'v2-metadata' | 'v1-estimated' | 'unknown';
  /**
   * True when fields were derived/estimated rather than authored. The UI
   * shows `(待 S3 補完)` so parents don't mistake estimates for canon.
   */
  pending: boolean;
  /** Subject this KP belongs to (for grouping). 'unknown' as last resort. */
  subject: Subject | 'unknown';
  /** V1 chapter title — handy for the recent-units list. */
  chapterTitle?: string;
  /** V1 chapter number. */
  chapterNumber?: number;
}

/**
 * One row of the 能力指標達成度 panel: a single 學習表現 code (verb-layer)
 * + a 學生能 ___ sentence + percent-achievement across the KPs that map
 * to it. % = average of `masteryPercent(kp)` across rows with ≥1 attempt.
 */
export interface AbilityIndicatorRow {
  /** e.g. `'n-II-9'`. `'pending'` when no perf code derivable. */
  moePerformanceCode: string;
  /** 「學生能 ___」自然語句, e.g. 「學生能：認識同分母分數並比較大小」. */
  abilitySentence: string;
  /** 0-100. Averaged across KPs with totalAttempts ≥ 1. */
  achievementPct: number;
  /** Count of KPs grouped under this performance code. */
  kpCount: number;
  /** Count of KPs the student has practiced at least once. */
  practicedKPCount: number;
  /** True when this row was estimated rather than V2-authored. */
  pending: boolean;
}

/**
 * One row of the 最近一週學了什麼 list. Aggregates by {subject, chapter}
 * so a kid who hit 5 KPs in 第 4 章 shows ONE row, not 5.
 */
export interface RecentUnitRow {
  subject: Subject;
  chapterNumber: number;
  chapterTitle: string;
  /** Distinct KPs in this chapter practiced within the window. */
  kpCount: number;
  /** Sum of attempts (across those KPs) within the window. */
  attempts: number;
  /** Most recent lastReviewedISO across those KPs (YYYY-MM-DD). */
  lastReviewedISO: string;
  /** Best-effort weekRange string like `'w.13-14'`; '' when unknown. */
  weekRangeLabel: string;
}

// ────────────────────────────────────────────────────────────────────────
// Optional V2 metadata loader (graceful when file doesn't exist)
// ────────────────────────────────────────────────────────────────────────

/**
 * Shape we expect from `src/learn/curriculum/v2/moe-metadata.json` once
 * S3 ships. Keyed by kpId for O(1) lookup. Kept as a soft contract here
 * — if S3 picks a different shape we just won't find entries and fall
 * back to V1 estimation.
 */
interface V2MetadataFile {
  [kpId: string]: {
    moeContentCode: string;
    moePerformanceCode?: string;
    description?: string;
    abilitySentence?: string;
  };
}

let v2MetadataCache: V2MetadataFile | null = null;
let v2MetadataLoaded = false;

/**
 * Inject V2 metadata at runtime. Exported chiefly for tests; production
 * code should rely on the dynamic-import path below. Calling with `null`
 * clears the cache (used by tests to assert the V1 fallback path).
 */
export function _setV2MetadataForTesting(data: V2MetadataFile | null): void {
  v2MetadataCache = data;
  v2MetadataLoaded = true;
}

/**
 * Best-effort sync read. We deliberately do NOT add a top-level
 * `import './v2/moe-metadata.json'` because:
 *   (a) the file may not exist yet (S3 unship),
 *   (b) bundlers vary on JSON imports under "moduleResolution: bundler".
 *
 * Tests that need V2-path coverage use {@link _setV2MetadataForTesting}.
 * Production wiring (when S3 ships) should add an explicit static import
 * in a thin loader module and pipe through `_setV2MetadataForTesting`.
 */
function getV2Metadata(): V2MetadataFile | null {
  if (!v2MetadataLoaded) {
    // No dynamic-import side-effects at module init — keep helpers pure
    // until S3 wires the loader. Mark loaded so we don't re-check.
    v2MetadataLoaded = true;
  }
  return v2MetadataCache;
}

// ────────────────────────────────────────────────────────────────────────
// V1 → 學習表現 (performance code) estimator
// ────────────────────────────────────────────────────────────────────────

/**
 * Best-effort derivation of 學習表現 code from 學習內容 code.
 *
 * 課綱 convention (CURRICULUM.md §對照說明):
 *   • 學習內容: `<UPPERCASE-STRAND>-<GRADE-DIGIT>-<SERIAL>` e.g. `N-3-7`
 *   • 學習表現: `<lowercase-strand>-<學習階段-Roman>-<SERIAL>` e.g. `n-II-7`
 *
 * 學習階段:
 *   - 3 / 4 → II  (第二學習階段)
 *   - 5 / 6 → III (第三學習階段)
 *
 * 'pending' moeCode → undefined (don't fake it).
 */
function estimatePerformanceCode(moeContentCode: string): string | undefined {
  if (!moeContentCode || moeContentCode === 'pending') return undefined;
  // Expect e.g. "N-3-7" or "INc-3-5" (multi-letter strand). Be permissive.
  const m = moeContentCode.match(/^([A-Za-z]+)-(\d+)-([\w.]+)$/);
  if (!m) return undefined;
  const [, strand, gradeStr, serial] = m;
  const grade = Number(gradeStr);
  const stage = grade <= 4 ? 'II' : grade <= 6 ? 'III' : 'IV';
  return `${strand.toLowerCase()}-${stage}-${serial}`;
}

/**
 * Build a 「學生能 ___」 sentence from KP label + performance code.
 * Honest stop-gap until S3 ships authored abilitySentence strings.
 *
 * Example: kpLabel = '同分母分數的意義' → '學生能：理解同分母分數的意義'.
 */
function defaultAbilitySentence(kpLabel: string): string {
  // 林媽媽 2026-05-21 complaint: parents must never see `math.foo.bar` style
  // raw dotted ids. Pass label through humaniseKpLabel so unknown KP fallback
  // (where lookupMoeMetadata sets label === kpId) becomes 「Math Foo Bar」
  // rather than leaking the dotted form into 「學生能：理解math.foo.bar」.
  return `學生能：理解${humaniseKpLabel(kpLabel)}`;
}

// ────────────────────────────────────────────────────────────────────────
// V1 walk index (built once for O(1) lookup)
// ────────────────────────────────────────────────────────────────────────

interface V1Index {
  byKpId: Map<string, { kp: KPEntry; chapter: ChapterEntry; subject: SubjectCurriculum }>;
}

let v1IndexCache: V1Index | null = null;

function getV1Index(): V1Index {
  if (v1IndexCache) return v1IndexCache;
  const byKpId = new Map<string, { kp: KPEntry; chapter: ChapterEntry; subject: SubjectCurriculum }>();
  for (const sc of CURRICULUM) {
    for (const ch of sc.chapters) {
      for (const kp of ch.kps) {
        byKpId.set(kp.id, { kp, chapter: ch, subject: sc });
      }
    }
  }
  v1IndexCache = { byKpId };
  return v1IndexCache;
}

/** Test-only: clear the cached index (used when stubbing CURRICULUM). */
export function _clearV1IndexForTesting(): void {
  v1IndexCache = null;
}

// ────────────────────────────────────────────────────────────────────────
// Public — lookupMoeMetadata
// ────────────────────────────────────────────────────────────────────────

/**
 * Resolve a kpId to its 教育部代碼 + description, preferring V2 metadata
 * when available, otherwise estimating from V1.
 *
 * Never throws. Returns an `unknown`-source row when the kpId is in
 * neither V2 nor V1 (e.g. legacy save imported from a removed bank).
 */
export function lookupMoeMetadata(kpId: string): MoeMetadata {
  // 1) V2 path
  const v2 = getV2Metadata();
  const v2Entry = v2?.[kpId];
  if (v2Entry) {
    const v1hit = getV1Index().byKpId.get(kpId);
    return {
      kpId,
      moeContentCode: v2Entry.moeContentCode,
      moePerformanceCode: v2Entry.moePerformanceCode,
      description: v2Entry.description ?? '',
      label: v1hit?.kp.label ?? kpId,
      source: 'v2-metadata',
      pending: false,
      subject: v1hit?.subject.subject ?? 'unknown',
      chapterTitle: v1hit?.chapter.title,
      chapterNumber: v1hit?.chapter.number,
    };
  }

  // 2) V1 fallback
  const v1hit = getV1Index().byKpId.get(kpId);
  if (v1hit) {
    const moeContent = v1hit.kp.moeCode ?? 'pending';
    const perf = estimatePerformanceCode(moeContent);
    return {
      kpId,
      moeContentCode: moeContent,
      moePerformanceCode: perf,
      description: '',
      label: v1hit.kp.label,
      source: 'v1-estimated',
      pending: true,
      subject: v1hit.subject.subject,
      chapterTitle: v1hit.chapter.title,
      chapterNumber: v1hit.chapter.number,
    };
  }

  // 3) unknown — never throw, never echo gibberish silently
  return {
    kpId,
    moeContentCode: 'pending',
    moePerformanceCode: undefined,
    description: '',
    label: kpId,
    source: 'unknown',
    pending: true,
    subject: 'unknown',
  };
}

/**
 * 「學生能：理解 ___」用 label 的人類可讀化包裝。
 * 若 label 本身就是 raw dotted id(unknown KP fallback case),
 * 把 `.`-split 後每段首字母大寫 + 用空格連回去,避免林媽媽 2026-05-21
 * complaint:「parent 永遠不該看到 `math.foo.bar`」。
 *
 * Pure helper,export 供 UI / dashboard / 其他 surface 共用。
 */
export function humaniseKpLabel(label: string): string {
  // Heuristic: looks-like-dotted-id when there's a `.` AND no whitespace AND
  // every dot-segment is lowercase ASCII/digits/underscore/dash. Otherwise
  // assume it's already an authored 中文 / 句子 label and return verbatim.
  const looksLikeDottedId = label.includes('.')
    && !/\s/.test(label)
    && label.split('.').every(seg => /^[a-z0-9_-]+$/i.test(seg));
  if (!looksLikeDottedId) return label;
  return label
    .split('.')
    .map(seg => (seg.length === 0 ? seg : seg[0].toUpperCase() + seg.slice(1)))
    .join(' ');
}

// ────────────────────────────────────────────────────────────────────────
// Public — buildAbilityIndicatorAchievement
// ────────────────────────────────────────────────────────────────────────

/**
 * Group the student's KPs by `moePerformanceCode` and compute %
 * achievement per group. Achievement = average mastery percent across
 * KPs in the group that have ≥1 attempt. Untouched KPs are listed for
 * `kpCount` but excluded from the average so a brand-new player doesn't
 * see "0% 達成" on every indicator.
 *
 * Rows are sorted DESCENDING by achievementPct so the parent sees
 * "what the kid is best at" up top (less anxiety-inducing than weakest-
 * first, which is already covered by buildWeakKPTopN).
 *
 * Returns `[]` when the player has no studentKPs yet.
 */
export function buildAbilityIndicatorAchievement(
  progress: Pick<PlayerProgress, 'studentKPs'>,
): AbilityIndicatorRow[] {
  const studentKPs = progress.studentKPs ?? [];
  if (studentKPs.length === 0) return [];

  // group key = performance code (or 'pending' bucket so we don't lose KPs)
  type Bucket = {
    perfCode: string;
    sumMastery: number;
    practicedKPCount: number;
    kpCount: number;
    sampleLabel: string; // for fallback abilitySentence
    pending: boolean;
  };
  const groups = new Map<string, Bucket>();

  for (const sk of studentKPs) {
    const meta = lookupMoeMetadata(sk.kpId);
    const key = meta.moePerformanceCode ?? 'pending';
    let bucket = groups.get(key);
    if (!bucket) {
      bucket = {
        perfCode: key,
        sumMastery: 0,
        practicedKPCount: 0,
        kpCount: 0,
        sampleLabel: meta.label,
        pending: meta.pending || key === 'pending',
      };
      groups.set(key, bucket);
    }
    bucket.kpCount += 1;
    if (sk.totalAttempts >= 1) {
      bucket.sumMastery += masteryPercent(sk);
      bucket.practicedKPCount += 1;
    }
    // any non-pending member promotes the bucket out of pending
    if (!meta.pending && key !== 'pending') bucket.pending = false;
  }

  const rows: AbilityIndicatorRow[] = [];
  for (const b of groups.values()) {
    const achievementPct = b.practicedKPCount === 0
      ? 0
      : Math.round(b.sumMastery / b.practicedKPCount);
    rows.push({
      moePerformanceCode: b.perfCode,
      abilitySentence: defaultAbilitySentence(b.sampleLabel),
      achievementPct,
      kpCount: b.kpCount,
      practicedKPCount: b.practicedKPCount,
      pending: b.pending,
    });
  }

  // sort: highest achievement first; ties broken by more-practiced first
  rows.sort((a, b) => b.achievementPct - a.achievementPct || b.practicedKPCount - a.practicedKPCount);
  return rows;
}

// ────────────────────────────────────────────────────────────────────────
// Public — buildRecentWeekUnits
// ────────────────────────────────────────────────────────────────────────

/**
 * List the chapters / units the student practiced in the past 7 days
 * (or `windowDays` if overridden). Aggregates KPs by {subject, chapter}
 * so each row is one textbook unit, not one KP.
 *
 * Activity signal: `studentKP.lastReviewedISO` falls inside the window.
 * We choose this over `progress.dailyXP` because daily XP doesn't carry
 * KP attribution — we need per-KP timestamps to know which chapter was
 * actually touched.
 *
 * Sorted by most-recent activity first.
 */
export function buildRecentWeekUnits(
  progress: Pick<PlayerProgress, 'studentKPs'>,
  windowDays: number = 7,
  nowISO: string = new Date().toISOString(),
): RecentUnitRow[] {
  if (windowDays <= 0) return [];
  const studentKPs = progress.studentKPs ?? [];
  if (studentKPs.length === 0) return [];

  // window cutoff date (YYYY-MM-DD; inclusive)
  const cutoff = new Date(nowISO.slice(0, 10));
  cutoff.setUTCDate(cutoff.getUTCDate() - (windowDays - 1));
  const cutoffISO = cutoff.toISOString().slice(0, 10);

  type Key = string; // `${subject}::${chapterNumber}`
  const buckets = new Map<Key, RecentUnitRow>();

  for (const sk of studentKPs) {
    if (!sk.lastReviewedISO || sk.lastReviewedISO === '') continue;
    const reviewedDay = sk.lastReviewedISO.slice(0, 10);
    if (reviewedDay < cutoffISO) continue;
    const meta = lookupMoeMetadata(sk.kpId);
    if (meta.subject === 'unknown' || meta.chapterNumber === undefined) continue;

    const key: Key = `${meta.subject}::${meta.chapterNumber}`;
    let row = buckets.get(key);
    if (!row) {
      row = {
        subject: meta.subject,
        chapterNumber: meta.chapterNumber,
        chapterTitle: meta.chapterTitle ?? `第 ${meta.chapterNumber} 章`,
        kpCount: 0,
        attempts: 0,
        lastReviewedISO: reviewedDay,
        // V1 has no weekRange. Leave blank — V2 lookup will fill once S2 wires runtime data.
        weekRangeLabel: '',
      };
      buckets.set(key, row);
    }
    row.kpCount += 1;
    row.attempts += sk.totalAttempts;
    if (reviewedDay > row.lastReviewedISO) row.lastReviewedISO = reviewedDay;
  }

  return Array.from(buckets.values()).sort((a, b) => {
    if (b.lastReviewedISO !== a.lastReviewedISO) {
      return b.lastReviewedISO < a.lastReviewedISO ? -1 : 1;
    }
    return b.attempts - a.attempts;
  });
}
